import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useTag } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { TagForm } from '../../components/tags';
import { updateTag, ApiError } from '../../lib/apiClient';
import type { TagFormData, TagFormCategory } from '../../components/tags/TagForm';
import type { TagCategory } from '../../graphql/types';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';

const API_BASE_URL = import.meta.env.VITE_REST_API_URL || 'http://localhost:3001/api/v1';

export const Route = createFileRoute('/tags/$tagId/edit')({
  component: EditTagPage,
});

export { EditTagPage };

function EditTagPage(): JSX.Element {
  const { tagId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<TagFormCategory[]>([]);

  const { data, loading, error: fetchError } = useTag(tagId);
  const tag = data?.tag;

  useEffect(() => {
    fetch(`${API_BASE_URL}/tag-categories`, { credentials: 'include' })
      .then((res) => (res.ok ? (res.json() as Promise<{ data: TagCategory[] }>) : { data: [] }))
      .then((json) => {
        setCategories((json as { data: TagCategory[] }).data);
      })
      .catch(() => {
        setCategories([]);
      });
  }, []);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  if (loading) {
    return <div className="edit-tag-page" />;
  }

  if (fetchError || !tag) {
    return (
      <div className="edit-tag-page">
        <p className="edit-tag-page__error">Failed to load tag</p>
      </div>
    );
  }

  const handleSubmit = async (data: TagFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await updateTag(tagId, {
        name: data.name,
        color: data.color || undefined,
        description: data.description || undefined,
        value: data.value,
      });
      await apolloClient.refetchQueries({ include: ['GetTag', 'ListTags'] });
      // Land back on the tag's own category (#715 — the list page no longer
      // has an "All Categories" view, so an empty categoryId would default to
      // the first-listed category, which may not be this tag's).
      void navigate({
        to: '/tags',
        search: { page: 1, pageSize: 25, categoryId: tag.categoryId ?? '' },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({
      to: '/tags',
      search: { page: 1, pageSize: 25, categoryId: tag.categoryId ?? '' },
    });
  };

  return (
    <div className="edit-tag-page">
      <div className="breadcrumb">
        <Link
          to="/tags"
          search={{ page: 1, pageSize: 25, categoryId: tag.categoryId ?? '' }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Tags</span>
        </Link>
      </div>

      <div className="page-header">
        <h1 className="forge-typography--headline4">Edit Tag</h1>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TagForm
        mode="edit"
        defaultValues={{
          name: tag.name,
          categoryId: tag.categoryId ?? undefined,
          color: tag.color ?? undefined,
          description: tag.description ?? undefined,
          value: tag.value ?? undefined,
        }}
        categories={categories}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
